import { inject, Injectable } from '@angular/core';
import { ApplicationService } from '../application/application.service';
import { filter, Observable, switchMap, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import {
    SubmissionToFccAttachmentRequiredDialogMessage,
    SubmissionToFccCompleteDialogMessage,
    SubmissionToFccFailedValidationDialogMessage,
    SubmissionToFccNoSelectedFrequenciesDialogMessage,
} from '../../constants/submission-dialog-message';
import { ApplicationUserEmailVerificationService } from '../../../../shared/services/application-user-email-verification/application-user-email-verification.service';
import {
    ApplicationSubmitToFccResponse,
    ApplicationSubmitToFccStatus,
} from '../../models/responses/application-submit-to-fcc-response';
import { CheckmarkDialogComponent } from '../../../../../shared/components/checkmark-dialog/checkmark-dialog.component';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../../core/constants/routes';
import { SubmitToFiveDayHoldRequest } from '../../models/requests/submit-to-five-day-hold-request';
import { SubmitToFiveDayHoldDialogComponent } from '../../components/submit-to-five-day-hold-dialog/submit-to-five-day-hold-dialog.component';
import { ApplicationSubmitToFccRequestModel } from './models/application-submit-to-fcc-request-model';

@Injectable({
    providedIn: 'root',
})
export class ApplicationSubmitToFccService {
    private readonly applicationUserEmailVerificationService = inject(
        ApplicationUserEmailVerificationService,
    );
    private readonly applicationService = inject(ApplicationService);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);

    submitToFcc(
        request: ApplicationSubmitToFccRequestModel,
    ): Observable<ApplicationSubmitToFccResponse> {
        return this.applicationUserEmailVerificationService
            .checkEmailVerification(request.applicationUserId)
            .pipe(
                // Only proceed with the submission if the user confirms (or if the dialog does not apply)
                filter((confirmed) => confirmed === true),
                switchMap(() => {
                    return this.applicationService
                        .submitToFcc(
                            request.applicationId,
                            request.skipFrequencySelectionValidation,
                        )
                        .pipe(
                            tap((response) => {
                                this.handleSubmissionStatus(
                                    response.submissionStatus,
                                    request.applicationId,
                                    request.showSuccessDialog ?? true,
                                );
                            }),
                        );
                }),
            );
    }

    submitToFiveDayHold(
        applicationId: string,
        request: SubmitToFiveDayHoldRequest,
    ): void {
        this.applicationService
            .submitToFiveDayHold(applicationId, request)
            .pipe(
                tap(() => {
                    this.openSubmissionCompleteDialog();
                }),
            )
            .subscribe();
    }

    openFiveDayHoldDialog(applicationId: string): void {
        this.dialog.open(SubmitToFiveDayHoldDialogComponent, {
            disableClose: true,
            data: {
                applicationId: applicationId,
                onSubmit: (request: SubmitToFiveDayHoldRequest) => {
                    this.submitToFiveDayHold(applicationId, request);
                    this.dialog.closeAll();
                },
            },
        });
    }

    private handleSubmissionStatus(
        submissionStatus: ApplicationSubmitToFccStatus,
        applicationId: string,
        showSuccessDialog: boolean,
    ): void {
        switch (submissionStatus) {
            case ApplicationSubmitToFccStatus.Submitted:
                if (showSuccessDialog) {
                    this.openSubmissionCompleteDialog();
                }
                return;
            case ApplicationSubmitToFccStatus.FailedValidation:
                this.openFailedValidationDialog();
                return;
            case ApplicationSubmitToFccStatus.NoSelectedFrequenciesError:
                this.openNoSelectedFrequenciesErrorDialog();
                return;
            case ApplicationSubmitToFccStatus.NoSelectedFrequenciesWarning:
                this.openNoSelectedFrequenciesWarningDialog(
                    applicationId,
                    showSuccessDialog,
                );
                return;
            case ApplicationSubmitToFccStatus.AttachmentRequired:
                this.openAttachmentRequiredDialog();
                return;
            default:
                throw new Error(
                    `Unknown submission status: ${submissionStatus}`,
                );
        }
    }

    private openFailedValidationDialog(): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: SubmissionToFccFailedValidationDialogMessage.modalTitle,
                content: SubmissionToFccFailedValidationDialogMessage.content,
                primaryAction: {
                    title: SubmissionToFccFailedValidationDialogMessage.primaryActionTitle,
                    action: this.dialog.closeAll.bind(this.dialog),
                },
                isShowCancel: false,
            },
        });
    }

    private openNoSelectedFrequenciesErrorDialog(): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: SubmissionToFccNoSelectedFrequenciesDialogMessage.modalTitle,
                content:
                    SubmissionToFccNoSelectedFrequenciesDialogMessage.errorContent,
                primaryAction: {
                    title: SubmissionToFccNoSelectedFrequenciesDialogMessage.primaryActionTitle,
                    action: this.dialog.closeAll.bind(this.dialog),
                },
                isShowCancel: false,
            },
        });
    }

    private openNoSelectedFrequenciesWarningDialog(
        applicationId: string,
        showSuccessDialog: boolean,
    ): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: SubmissionToFccNoSelectedFrequenciesDialogMessage.modalTitle,
                content:
                    SubmissionToFccNoSelectedFrequenciesDialogMessage.warningContent,
                primaryAction: {
                    title: SubmissionToFccNoSelectedFrequenciesDialogMessage.secondaryActionTitle,
                    action: () => {
                        this.applicationService
                            .submitToFcc(applicationId, true)
                            .pipe(
                                tap((response) => {
                                    this.handleSubmissionStatus(
                                        response.submissionStatus,
                                        applicationId,
                                        showSuccessDialog,
                                    );
                                }),
                            )
                            .subscribe();
                    },
                },
                isShowCancel: true,
                cancelTitle:
                    SubmissionToFccNoSelectedFrequenciesDialogMessage.primaryActionTitle, // Back to Application
            },
        });
    }

    private openAttachmentRequiredDialog(): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: SubmissionToFccAttachmentRequiredDialogMessage.modalTitle,
                content: SubmissionToFccAttachmentRequiredDialogMessage.content,
                isShowCancel: true,
            },
        });
    }

    private openSubmissionCompleteDialog(): void {
        this.dialog.open(CheckmarkDialogComponent, {
            disableClose: true,
            data: {
                title: SubmissionToFccCompleteDialogMessage.modalTitle,
                primaryAction: {
                    title: SubmissionToFccCompleteDialogMessage.primaryActionTitle,
                    action: this.navigateToDashboard.bind(this),
                },
            },
        });
    }

    private navigateToDashboard(): void {
        this.router.navigate([RouteConstants.Dashboard.getFullRoute('INDEX')]);
    }
}
